import { type ReactNode, useState } from "react";
import { Link } from "react-router-dom";

import {
  ArrowRight,
  ClipboardCheck,
  GitCompareArrows,
  LineChart,
  MessageSquareText,
  Minus,
  Plus,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface Feature {
  id: string;
  eyebrow: string;
  title: string;
  icon: LucideIcon;
  image?: string;
  visual?: ReactNode;
  description: string;
  benefits: string[];
  to: string;
  cta: string;
}

const AUDIT_DIMENSIONS = [
  { label: "Answer Engine (AEO)", score: 72, color: "bg-primary" },
  { label: "Generative Search (GEO)", score: 64, color: "bg-secondary" },
  { label: "AI Integration (AIO)", score: 58, color: "bg-tertiary" },
  { label: "Crawlability", score: 81, color: "bg-emerald-500" },
  { label: "Search Experience (SXO)", score: 69, color: "bg-blue-500" },
];

const AuditScoreVisual = () => (
  <div className="rounded-[2.5rem] shadow-2xl w-full ring-1 ring-outline-variant/10 bg-surface-container-low p-6 space-y-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs font-bold text-primary uppercase tracking-tighter mb-1">
          LLM SEO Score
        </p>
        <div className="flex items-end gap-2">
          <span className="font-headline text-5xl font-extrabold text-on-surface">68</span>
          <span className="text-on-surface-variant text-lg font-medium mb-1">/ 100</span>
        </div>
      </div>
      <div className="text-right">
        <span className="inline-block px-3 py-1 rounded-full bg-yellow-500/15 text-yellow-500 text-xs font-bold">
          Needs Work
        </span>
      </div>
    </div>
    <div className="h-px bg-surface-container-high" />
    <div className="space-y-3">
      {AUDIT_DIMENSIONS.map((d) => (
        <div key={d.label}>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-on-surface-variant font-medium">{d.label}</span>
            <span className="font-bold text-on-surface">{d.score}</span>
          </div>
          <div className="h-1.5 w-full bg-surface-container-high rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${d.color}`} style={{ width: `${d.score}%` }} />
          </div>
        </div>
      ))}
    </div>
    <p className="text-xs text-on-surface-variant/60 text-center">
      Example audit — your score will reflect your actual site
    </p>
  </div>
);

const LLM_RANKING_EXAMPLES = [
  {
    provider: "ChatGPT",
    color: "text-green-400",
    bg: "bg-green-500/10",
    border: "border-green-500/20",
    hotels: ["The Ritz Paris", "Hotel Meurice", "Grand Palace Hotel", "Le Bristol Paris"],
    highlighted: 2,
  },
  {
    provider: "Gemini",
    color: "text-blue-400",
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
    hotels: ["Grand Palace Hotel", "Hotel Meurice", "Le Bristol Paris"],
    highlighted: 0,
  },
  {
    provider: "Claude",
    color: "text-orange-400",
    bg: "bg-orange-500/10",
    border: "border-orange-500/20",
    hotels: ["Hotel Meurice", "Grand Palace Hotel", "The Ritz Paris"],
    highlighted: 1,
  },
];

const LLMVisibilityVisual = () => (
  <div className="rounded-[2.5rem] shadow-2xl w-full ring-1 ring-outline-variant/10 bg-surface-container-low p-6 space-y-3">
    <p className="text-xs text-on-surface-variant font-medium">
      Example: &ldquo;luxury hotels in Paris, France&rdquo;
    </p>
    {LLM_RANKING_EXAMPLES.map((engine) => (
      <div key={engine.provider} className={`rounded-2xl border ${engine.border} ${engine.bg} p-4`}>
        <div className={`text-xs font-bold uppercase tracking-widest mb-3 ${engine.color}`}>
          {engine.provider}
        </div>
        <div className="space-y-1.5">
          {engine.hotels.map((hotel, i) => (
            <div
              key={hotel}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs ${
                i === engine.highlighted
                  ? "bg-primary/10 ring-1 ring-primary/30"
                  : "bg-surface-container-lowest/50"
              }`}
            >
              <span className="text-on-surface-variant/50 font-mono w-4 shrink-0">{i + 1}.</span>
              <span
                className={`truncate flex-1 ${i === engine.highlighted ? "font-bold text-primary" : "text-on-surface"}`}
              >
                {hotel}
              </span>
              {i === engine.highlighted && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-primary/20 text-primary shrink-0">
                  You
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    ))}
    <p className="text-xs text-on-surface-variant/60 text-center">
      Example data — your report will show real results
    </p>
  </div>
);

const OTA_EXAMPLES = [
  {
    provider: "ChatGPT",
    color: "text-green-400",
    bg: "bg-green-500/10",
    border: "border-green-500/20",
    links: [
      { url: "booking.com/hotel/grand-palace", type: "ota" },
      { url: "grandpalacehotel.com", type: "direct" },
      { url: "expedia.com/hotels/grand-palace", type: "ota" },
    ],
  },
  {
    provider: "Gemini",
    color: "text-blue-400",
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
    links: [
      { url: "tripadvisor.com/grand-palace-hotel", type: "ota" },
      { url: "grandpalacehotel.com/rooms", type: "direct" },
      { url: "booking.com/hotel/grand-palace", type: "ota" },
    ],
  },
  {
    provider: "Claude",
    color: "text-orange-400",
    bg: "bg-orange-500/10",
    border: "border-orange-500/20",
    links: [
      { url: "grandpalacehotel.com", type: "direct" },
      { url: "hotels.com/grand-palace", type: "ota" },
      { url: "agoda.com/grand-palace", type: "ota" },
    ],
  },
];

const OTAExampleVisual = () => (
  <div className="rounded-[2.5rem] shadow-2xl w-full ring-1 ring-outline-variant/10 bg-surface-container-low p-6 space-y-3">
    <p className="text-xs text-on-surface-variant font-medium">
      Example: &ldquo;book Grand Palace Hotel in Paris, France&rdquo;
    </p>
    {OTA_EXAMPLES.map((result) => (
      <div key={result.provider} className={`rounded-2xl border ${result.border} ${result.bg} p-4`}>
        <div className={`text-xs font-bold uppercase tracking-widest mb-3 ${result.color}`}>
          {result.provider}
        </div>
        <div className="space-y-2">
          {result.links.map((link, i) => (
            <div
              key={i}
              className="flex items-center gap-2 p-2 rounded-lg bg-surface-container-lowest/50"
            >
              <span className="text-on-surface-variant/40 text-xs font-mono w-4 shrink-0">
                {i + 1}.
              </span>
              <span className="text-on-surface text-xs font-mono truncate flex-1">{link.url}</span>
              <span
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
                  link.type === "direct"
                    ? "bg-emerald-500/20 text-emerald-400"
                    : "bg-red-500/20 text-red-400"
                }`}
              >
                {link.type === "direct" ? "Direct" : "OTA"}
              </span>
            </div>
          ))}
        </div>
      </div>
    ))}
    <p className="text-xs text-on-surface-variant/60 text-center">
      Example data — your report will show real results
    </p>
  </div>
);

const PERSONA_EXAMPLES = [
  { emoji: "💑", label: "Couples", score: 84 },
  { emoji: "👨‍👩‍👧", label: "Families", score: 61 },
  { emoji: "💼", label: "Business", score: 47 },
  { emoji: "🎒", label: "Solo", score: 72 },
];

const PersonaExampleVisual = () => (
  <div className="rounded-[2.5rem] shadow-2xl w-full ring-1 ring-outline-variant/10 bg-surface-container-low p-6 space-y-3">
    <p className="text-xs text-on-surface-variant font-medium">
      Example: visibility by traveler persona
    </p>
    <div className="bg-surface-container-lowest rounded-2xl border border-surface-container-high p-6 space-y-3">
      {PERSONA_EXAMPLES.map((p) => (
        <div key={p.label} className="flex items-center gap-3">
          <span className="text-2xl">{p.emoji}</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">{p.label}</div>
            <div className="mt-1.5 h-1.5 w-full bg-surface-container-high rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${
                  p.score >= 60
                    ? "bg-emerald-500"
                    : p.score >= 50
                      ? "bg-yellow-500"
                      : "bg-orange-500"
                }`}
                style={{ width: `${p.score}%` }}
              />
            </div>
          </div>
          <div
            className={`text-lg font-extrabold font-headline shrink-0 ${
              p.score >= 60
                ? "text-emerald-400"
                : p.score >= 50
                  ? "text-yellow-400"
                  : "text-orange-400"
            }`}
          >
            {p.score}
          </div>
        </div>
      ))}
    </div>
    <p className="text-xs text-on-surface-variant/60 text-center">
      Example data — your report will show real results
    </p>
  </div>
);

const GA_KPIS = [
  { label: "Sessions", value: "12,480", delta: "+18.2%", up: true },
  { label: "Users", value: "9,317", delta: "+12.6%", up: true },
  { label: "Conversions", value: "412", delta: "+24.9%", up: true },
  { label: "Bounce rate", value: "38.4%", delta: "-4.1%", up: true },
];

// A visual stand-in for the trend chart, not real data — the bars are a fixed
// silhouette chosen to read as growth at a glance.
const GA_TREND = [38, 44, 41, 52, 49, 61, 58, 70, 66, 78, 83, 92];

const GoogleAnalyticsVisual = () => (
  <div className="rounded-[2.5rem] shadow-2xl w-full ring-1 ring-outline-variant/10 bg-surface-container-low p-6 space-y-4">
    <div className="flex items-center justify-between">
      <p className="text-xs text-on-surface-variant font-medium">Example: last 28 days</p>
      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
        GA4 connected
      </span>
    </div>

    <div className="grid grid-cols-2 gap-3">
      {GA_KPIS.map((kpi) => (
        <div
          key={kpi.label}
          className="bg-surface-container-lowest rounded-2xl border border-surface-container-high p-4"
        >
          <p className="text-xs text-on-surface-variant truncate">{kpi.label}</p>
          <p className="font-headline text-2xl font-extrabold text-on-surface mt-1">{kpi.value}</p>
          <p
            className={`text-xs font-bold mt-1 ${kpi.up ? "text-emerald-500" : "text-red-400"}`}
            aria-label={`${kpi.delta} versus the previous period`}
          >
            {kpi.delta} vs prev.
          </p>
        </div>
      ))}
    </div>

    <div className="bg-surface-container-lowest rounded-2xl border border-surface-container-high p-4">
      <p className="text-xs text-on-surface-variant mb-3">Sessions trend</p>
      <div className="flex items-end gap-1.5 h-20" aria-hidden="true">
        {GA_TREND.map((height, i) => (
          <div
            key={i}
            className="flex-1 bg-primary/70 rounded-t-sm"
            style={{ height: `${height}%` }}
          />
        ))}
      </div>
    </div>

    <p className="text-xs text-on-surface-variant/60 text-center">
      Example data — your dashboard will show your property&apos;s real numbers
    </p>
  </div>
);

const FEATURES: Feature[] = [
  {
    id: "ai-audit",
    eyebrow: "AI Audit",
    title: "Score your site the way AI engines read it",
    icon: ClipboardCheck,
    visual: <AuditScoreVisual />,
    description:
      "Our audit engine grades your hotel website across the five dimensions that decide whether an AI assistant recommends you — Answer Engine readiness (AEO), Generative search (GEO), AI integration (AIO), Crawlability, and Search experience (SXO).",
    benefits: [
      "One overall LLM SEO score plus a per-dimension breakdown",
      "A prioritized issue list with step-by-step fixes",
      "AI-generated schema and content suggestions you can deploy",
    ],
    to: "/audit",
    cta: "Run a free audit",
  },
  {
    id: "llm-visibility",
    eyebrow: "LLM Visibility",
    title: "See if ChatGPT, Gemini & Claude actually name you",
    icon: MessageSquareText,
    visual: <LLMVisibilityVisual />,
    description:
      "Run real guest prompts against the major AI engines and find out whether your hotel gets mentioned, where it ranks, and which competitor gets recommended in your place — across ChatGPT, Gemini, and Claude.",
    benefits: [
      "Prompt tests across the engines real travelers use",
      "Rank tracking so you know if you're 1st, 2nd or invisible",
      "Competitor comparisons that show who's winning the answer",
    ],
    to: "/llm-visibility",
    cta: "Test my visibility",
  },
  {
    id: "ota-direct",
    eyebrow: "OTA vs Direct",
    title: "Win the direct booking, not just the OTA listing",
    icon: GitCompareArrows,
    visual: <OTAExampleVisual />,
    description:
      "AI assistants often route guests to OTAs instead of your own site. We analyze how your direct presence stacks up against the OTAs in AI answers and surface the gaps costing you commission-free bookings.",
    benefits: [
      "OTA vs direct link analysis inside AI responses",
      "Gap analysis that pinpoints why OTAs get cited first",
      "Actions to reclaim the direct-booking recommendation",
    ],
    to: "/ota-direct",
    cta: "Compare OTA vs direct",
  },
  {
    id: "persona-visibility",
    eyebrow: "Persona Visibility",
    title: "Be the answer for every type of guest",
    icon: Users,
    visual: <PersonaExampleVisual />,
    description:
      "Different guests ask AI different questions. We test your visibility across eight guest personas — couples, families, solo travelers, luxury seekers and more — scoring mention, rank, fit, and cross-engine consistency for each.",
    benefits: [
      "Up to 8 guest personas analyzed per property",
      "Mention · Rank · Fit · Cross-engine scoring",
      "Composite persona trends you can track over time",
    ],
    to: "/persona",
    cta: "Explore personas",
  },
  {
    id: "google-analytics",
    eyebrow: "Google Analytics",
    title: "Prove what your AI visibility is actually worth",
    icon: LineChart,
    visual: <GoogleAnalyticsVisual />,
    description:
      "Connect your GA4 property in one read-only click and see traffic, engagement and conversions next to your AI reports — so a visibility win stops being a score and starts being bookings you can point at.",
    benefits: [
      "Sessions, users, conversions and engagement with period-over-period deltas",
      "Acquisition channels, top pages and top landing pages",
      "Read-only access to the GA4 properties you choose — disconnect any time",
    ],
    to: "/google-analytics",
    cta: "Connect Google Analytics",
  },
];

interface Faq {
  q: string;
  a: string;
}

const FAQS: Faq[] = [
  {
    q: "Is the free Discover plan really free?",
    a: "Yes — free forever, no card required. Discover gives you one full AI audit a month, your overall LLM SEO score, three prompt tests, and one persona analysis. Most hotels find enough on their first audit to want the full fix list, but if you don't, the free plan stays free.",
  },
  {
    q: "How is this different from regular SEO?",
    a: "Traditional SEO optimizes for Google's blue links. We optimize for AI assistants — ChatGPT, Gemini, Claude — which read, summarize, and recommend. The signals overlap (schema, structured content, authority) but the playbook is different. Our dual-mode strategy slider lets you balance both based on where your bookings come from.",
  },
  {
    q: "What if I'm not technical?",
    a: "That's most of our customers. Every recommendation in the AI Audit comes with a plain-English, step-by-step guide, and many fixes ship as AI-generated “Suggested Changes” you can review and deploy. You never need to touch code you don't understand.",
  },
  {
    q: "Which AI engines do you actually test against?",
    a: "ChatGPT and Gemini on the Visible plan, with Claude added (all three) from Optimized up, plus cross-engine analysis on Dominant. Enterprise can add Perplexity, Microsoft Copilot, and regional engines on request.",
  },
  {
    q: "What do the features actually cover?",
    a: "AI Audit scores your website; LLM Visibility tests whether the engines name you; OTA vs Direct shows whether AI sends guests to OTAs instead of your site; Persona Visibility checks how you show up for each type of guest; and the Google Analytics dashboard connects your GA4 property so you can see the traffic and conversions those wins produce. Together they map your entire AI-search presence and what it earns.",
  },
  {
    q: "I run an agency. Which plan should I be on?",
    a: "If you manage 1-3 hotels, start on Optimized. Managing 4-10? Dominant is built for you — five seats, white-label PDF reports, and access to the Certified Agency Partner program with a public directory listing. Over 10 clients or need reseller terms? Talk to us about Enterprise.",
  },
  {
    q: "How long until I see results?",
    a: "Visibility in AI answers updates faster than Google rankings — sometimes within days of a schema fix, usually within 30-60 days for content changes. Most customers see meaningful score improvement within eight weeks, and every win shows up immediately in your trend view.",
  },
];

const HeroSection = () => (
  <section className="relative overflow-hidden pt-16 pb-28 px-8">
    <div className="absolute -top-24 -right-24 w-[32rem] h-[32rem] bg-primary/5 rounded-full blur-3xl" />
    <div className="max-w-8xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center relative">
      <div className="lg:col-span-7">
        <span className="inline-block py-1 px-3 bg-surface-container-high text-primary font-label text-xs font-bold tracking-widest uppercase rounded-full mb-6">
          AI-Era Search Intelligence for Hotels
        </span>
        <h1 className="font-headline text-5xl lg:text-7xl font-extrabold text-on-surface leading-[1.1] mb-8 tracking-tight">
          Become the hotel <br />
          <span className="text-primary italic">AI recommends.</span>
        </h1>
        <p className="text-on-surface-variant text-xl max-w-xl mb-10 leading-relaxed">
          ChatGPT, Gemini and Claude are the new front desk. We audit your site, test your
          visibility, and show you exactly how to become the answer travelers get — across every AI
          engine and every type of guest.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            to="/audit"
            className="bg-primary text-on-primary px-8 py-4 rounded-xl font-bold text-lg hover:shadow-lg hover:shadow-primary/20 transition-all flex items-center justify-center gap-2"
          >
            Run a free audit
            <ArrowRight className="w-5 h-5" />
          </Link>
          <Link
            to="/pricing"
            className="border border-outline-variant text-on-surface px-8 py-4 rounded-xl font-bold text-lg hover:bg-surface-container-high transition-colors flex items-center justify-center"
          >
            See pricing
          </Link>
        </div>
      </div>
      <div className="lg:col-span-5 relative">
        <div className="aspect-square bg-surface-container-low rounded-[2rem] overflow-hidden relative shadow-2xl">
          <img
            src="/hero_hotel.jpg"
            alt="Minimalist luxury hotel lobby with soft natural lighting"
            className="w-full h-full object-cover grayscale-[20%] contrast-[1.1]"
            loading="lazy"
            width={800}
            height={800}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
        </div>
      </div>
    </div>
  </section>
);

const FeatureSection = ({ feature, index }: { feature: Feature; index: number }) => {
  const Icon = feature.icon;
  const imageFirst = index % 2 === 1;
  return (
    <section className="py-20 px-8">
      <div className="max-w-8xl mx-auto flex flex-col lg:flex-row gap-16 items-center">
        <div className={`lg:w-1/2 ${imageFirst ? "lg:order-1" : "lg:order-2"}`}>
          <div className="relative">
            {feature.visual ?? (
              <img
                src={feature.image}
                alt={feature.title}
                className="rounded-[2.5rem] shadow-2xl w-full object-cover aspect-[4/3] bg-surface-container ring-1 ring-outline-variant/10"
                loading="lazy"
              />
            )}
            <div className="absolute -top-10 -left-10 w-40 h-40 bg-primary/5 rounded-full blur-3xl" />
          </div>
        </div>
        <div className={`lg:w-1/2 ${imageFirst ? "lg:order-2" : "lg:order-1"}`}>
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-surface-container-high text-primary">
              <Icon className="w-5 h-5" />
            </span>
            <span className="font-label text-xs font-extrabold tracking-widest uppercase text-primary">
              {feature.eyebrow}
            </span>
          </div>
          <h2 className="font-headline text-3xl lg:text-4xl font-extrabold mb-5 leading-tight text-on-surface">
            {feature.title}
          </h2>
          <p className="text-on-surface-variant text-lg mb-7 leading-relaxed">
            {feature.description}
          </p>
          <ul className="space-y-3 mb-8">
            {feature.benefits.map((b) => (
              <li key={b} className="flex items-start gap-3 text-on-surface">
                <span className="mt-1 w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold">
                  ✓
                </span>
                <span className="font-medium">{b}</span>
              </li>
            ))}
          </ul>
          <Link
            to={feature.to}
            className="inline-flex items-center gap-2 text-primary font-bold hover:gap-3 transition-all"
          >
            {feature.cta}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const FaqItem = ({ faq }: { faq: Faq }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-surface-container-high">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 py-6 text-left"
      >
        <span className="font-headline text-lg font-bold text-on-surface">{faq.q}</span>
        <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-surface-container-high text-primary">
          {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </span>
      </button>
      {open && (
        <p className="pb-6 -mt-1 text-on-surface-variant leading-relaxed max-w-3xl">{faq.a}</p>
      )}
    </div>
  );
};

const FaqSection = () => (
  <section className="py-24 px-8" id="faq">
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-12">
        <span className="font-label text-xs font-bold tracking-widest uppercase text-primary">
          Frequently asked
        </span>
        <h2 className="font-headline text-4xl lg:text-5xl font-extrabold text-on-surface mt-3">
          Questions worth answering.
        </h2>
      </div>
      <div>
        {FAQS.map((faq) => (
          <FaqItem key={faq.q} faq={faq} />
        ))}
      </div>
    </div>
  </section>
);

const HomeCTASection = () => (
  <section className="py-24 px-8">
    <div className="max-w-4xl mx-auto bg-gradient-to-br from-primary to-primary-container p-16 rounded-[3rem] text-center text-on-primary relative overflow-hidden shadow-2xl">
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="relative z-10">
        <h2 className="font-headline text-4xl font-extrabold mb-6">
          Stop being the recommendation nobody makes.
        </h2>
        <p className="text-on-primary/80 text-lg mb-10 max-w-xl mx-auto">
          Run your free audit in under two minutes. See your score, your missing schema, and which
          competitor the AI named instead of you.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Link
            to="/audit"
            className="bg-on-primary text-primary px-10 py-4 rounded-xl font-bold text-lg hover:bg-inverse-on-surface transition-colors"
          >
            Run my free audit
          </Link>
          <Link
            to="/pricing"
            className="border border-on-primary/30 text-on-primary px-10 py-4 rounded-xl font-bold text-lg hover:bg-on-primary/10 transition-colors"
          >
            See pricing
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const HomePage = () => {
  return (
    <>
      <HeroSection />
      {FEATURES.map((feature, index) => (
        <FeatureSection key={feature.id} feature={feature} index={index} />
      ))}
      <FaqSection />
      <HomeCTASection />
    </>
  );
};

export default HomePage;
